import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ChartDeferred from 'chartjs-plugin-deferred';

const plugin = {
  id: 'customCanvasBackgroundColor',
  beforeDraw: (chart, args, options) => {
    const {ctx} = chart;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = options.color || '#ffffff';
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();
  }
};

export class ChartClass {

  constructor() {
    this.chartSelector = document.querySelectorAll('[data-chart-canvas]');
    this.init();
  }

  getCurrentBreakpoint() {
    return window.getComputedStyle(document.documentElement).getPropertyValue('--current-breakpoint').toString().replaceAll('"','');
  }

  downloadCanvas() {
    const canvas = document.querySelector('[data-chart-canvas=' + this.dataset.chartDownload + ']');
    const myChart = Chart.getChart(canvas);
    // Update padding
    myChart.options.layout.padding = 10;
    myChart.update('none');
    // create imgage from canvas
    let dt = canvas.toDataURL('image/png');
    dt = dt.replace(/^data:image\/[^;]*/, 'data:application/octet-stream');
    dt = dt.replace(/^data:application\/octet-stream/, 'data:application/octet-stream;headers=Content-Disposition%3A%20attachment%3B%20filename=Canvas.png');
    // Update padding
    myChart.options.layout.padding = 0;
    myChart.update('none');
    // Return href value
    this.href = dt;
  };

  init() {
    let charts = [];

    // Build Chart
    this.chartSelector.forEach((chartCanvas) => {
      let chartOptions = {
        title: chartCanvas.dataset.chartTitle,
        subtitle: chartCanvas.dataset.chartSubtitle,
        type: chartCanvas.dataset.chartType,
        simpleLabel: Boolean(parseInt(chartCanvas.dataset.chartSimpleLabel)),
        forceDecimalNumbers: chartCanvas.dataset.chartForceDecimalNumbers,
        legend: {
          position: chartCanvas.dataset.chartLegendPosition,
          visible: Boolean(parseInt(chartCanvas.dataset.chartLegendShow)),
          allowFiltering: Boolean(parseInt(chartCanvas.dataset.chartAllowFiltering))
        },
        data: JSON.parse(chartCanvas.dataset.chartData)
      }

      // Chart
      let chart = new Chart(chartCanvas.getContext('2d'), {
        type: chartOptions.type,
        data: chartOptions.data,
        options: this.getOptions(chartOptions, chartCanvas.getContext('2d')),
        plugins: [
          ChartDataLabels,
          ChartDeferred,
          plugin
        ]
      });
      charts.push(chart);

      // Download button
      document.querySelector('[data-chart-download=' + chartCanvas.dataset.chartCanvas + ']')
          .addEventListener('click', this.downloadCanvas, false);
    });
  }

  getOptions(chartOptions, ctx) {
    const _this = this;
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      aspectRatio: 0.6,
      datasets: {
        doughnut: {
          cutout: "70%"
        }
      },
      onResize: (chart, size) => {
        // Breakpoint
        let currentBreakpoint = this.getCurrentBreakpoint();
        if (['default', 'sm', 'md', 'lg', 'xl'].includes(currentBreakpoint)) {
          // Title
          chart.options.plugins.title.font.size = '24px';
          chart.options.plugins.title.font.lineHeight = '28px';
          // Subtitle
          chart.options.plugins.subtitle.font.size = '20px';
          chart.options.plugins.subtitle.font.lineHeight = '24px';
        }
        if (['sm', 'md', 'lg', 'xl'].includes(currentBreakpoint)) {
          // Title
          chart.options.plugins.title.font.size = '30px';
          chart.options.plugins.title.font.lineHeight = '39px';
        }
        if (['md', 'lg', 'xl'].includes(currentBreakpoint)) {
          // Title
          chart.options.plugins.title.font.size = '32px';
          chart.options.plugins.title.font.lineHeight = '42px';
          // Subtitle
          chart.options.plugins.subtitle.font.size = '24px';
          chart.options.plugins.subtitle.font.lineHeight = '28px';
        }

        // Relative chart size
        if (size.width <= 690) {
          // Legend
          chart.options.plugins.legend.position = 'bottom';
          chart.options.plugins.legend.align = 'start';
        } else {
          // Legend
          chart.options.plugins.legend.position = chart.canvas.dataset.chartLegendPosition;
          chart.options.plugins.legend.align = (chartOptions.legend.position === 'right') ? 'center' : 'start';
        }
      },
      scales: {
        x: {
          display: (chartOptions.type !== 'doughnut'),
          grid: {
            drawOnChartArea: false,
            color: '#ffffff'
          },
          ticks: {
            callback: function(value, index, ticks) {
              const label = this.getLabelForValue(value);
              if (label.includes("<format>")) {
                let unformattedValue = label.substring(label.indexOf("<format>") + "<format>".length, label.indexOf("</format>"));
                let formattedValue = _this.formatValue(unformattedValue.replace(',', '.'));
                return label.replace(label.substring(label.indexOf("<format>"), label.indexOf("</format>") + "</format>".length), formattedValue);
              }
              return label;
            }
          }
        },
        y: {
          display: (chartOptions.type !== 'doughnut'),
          grid: {
            color: '#cdcdcd'
          }
        }
      },
      resizeDelay: 250,
      plugins: {
        title: {
          display: !!(chartOptions.title),
          text: () => {
            let text = chartOptions.title;
            return this.wrapText(
                ctx,
                text,
                ctx.canvas.width,
                '300 32px ContinentalStagSansW'
            );
          },
          color: "#000",
          align: 'start',
          font: {
            size: '32px',
            weight: 300,
            lineHeight: '42px',
            fontFamily: "'ContinentalStagSansW','-apple-system','BlinkMacSystemFont','Segoe UI','Roboto','Helvetica Neue','Arial','Noto Sans',sans-serif"
          },
          padding: {
            bottom: (chartOptions.subtitle) ? 0 : 10
          }
        },
        subtitle: {
          display: !!(chartOptions.subtitle),
          text: () => {
            let text = chartOptions.subtitle;
            return this.wrapText(
                ctx,
                text,
                ctx.canvas.width,
                '400 24px ContinentalStagSansW'
            );
          },
          position: "top",
          color: "#969696",
          align: "start",
          font: {
            size: '24px',
            weight: 400,
            lineHeight: '28px',
            fontFamily: "'ContinentalStagSansW','-apple-system','BlinkMacSystemFont','Segoe UI','Roboto','Helvetica Neue','Arial','Noto Sans',sans-serif"
          },
          padding: {
            bottom: 20
          }
        },
        legend: {
          onClick: null,
          display: (chartOptions.type === 'doughnut') ? chartOptions.legend.visible : false,
          position: chartOptions.legend.position,
          align: (chartOptions.legend.position === 'right') ? 'center' : 'start',
          labels: {
            generateLabels: (chart) => {
              const data = chart.data;
              if (data.labels.length && data.datasets.length) {
                const { labels: { pointStyle , color  }  } = chart.legend.options;
                return data.labels.map((label, i) => {
                  const meta = chart.getDatasetMeta(0);
                  const style = meta.controller.getStyle(i);
                  if (label.includes("<format>")) {
                    let value = label.substring(label.indexOf("<format>") + "<format>".length, label.indexOf("</format>"));
                    let formattedValue = this.formatValue(value.replace(',', '.'));
                    label = label.replace(label.substring(label.indexOf("<format>"), label.indexOf("</format>") + "</format>".length), formattedValue);
                  }
                  return {
                    text: label,
                    fillStyle: style.backgroundColor,
                    strokeStyle: style.borderColor,
                    fontColor: color,
                    lineWidth: style.borderWidth,
                    pointStyle: pointStyle,
                    hidden: !chart.getDataVisibility(i),
                    index: i
                  };
                });
              }
            },
            color: "rgb(107,107,107)",
            padding: 10,
            boxWidth: 46,
            boxHeight: 14,
            font: {
              size: 16,
              weight: 300,
              family: 'ContinentalStagSansW,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif'
            },
          },
        },
        datalabels: {
          display: true,
          color: "#ffffff",
          backgroundColor: "rgba(0,0,0,.5)",
          borderRadius: 3,
          anchor: (chartOptions.type === 'doughnut') ? "center" : "end",
          formatter: (data) => {
            let label = "";
            let formattedValue = this.formatValue(data.value, chartOptions.forceDecimalNumbers);

            if (data.label.includes("<format>")) {
              label = data.label.replace(data.label.substring(data.label.indexOf("<format>"), data.label.indexOf("</format>") + "</format>".length), formattedValue);
            } else {
              if (chartOptions.type === 'bar' || chartOptions.simpleLabel === true) {
                label = formattedValue;
              } else {
                label = data.label + ": " + formattedValue;
              }
            }
            return label;
          }
        },
        tooltip: {
          enabled: false
        },
        deferred: {
          xOffset: 150,
          yOffset: '50%',
          delay: 250
        }
      }
    };

    // Delete onClick override, if default sorting is allowed
    // The other way would be better, but the example from the docs does not work.
    if (chartOptions.legend.allowFiltering === true) {
      delete options['plugins']['legend']['onClick'];
    }

    return options;
  }

  formatValue(value, forceDecimalNumbers) {
    // Get language from lang attribute in html tag
    const language = document.documentElement.lang;
    let numberFormatter = Intl.NumberFormat(language, {
      style: "decimal",
      minimumFractionDigits: forceDecimalNumbers,
      maximumFractionDigits: 2
    });

    return numberFormatter.format(value);
  }

  wrapText(ctx, text, maxWidth, font) {

    ctx.font = font;

    // First, start by splitting all of our text into words, but splitting it into an array split by spaces
    let words = text.split(' ');
    let line = ''; // This will store the text of the current line
    let testLine = ''; // This will store the text when we add a word, to test if it's too long
    let lineArray = []; // This is an array of lines, which the function will return

    for(let n = 0; n < words.length; n++) {
      // Create a test line, and measure it..
      testLine += `${words[n]} `;
      let metrics = ctx.measureText(testLine);
      let testWidth = metrics.width;
      // If the width of this test line is more than the max width
      if (testWidth > maxWidth && n > 0) {
        // Then the line is finished, push the current line into "lineArray"
        lineArray.push(line);
        // Update line and test line to use this word as the first word on the next line
        line = `${words[n]} `;
        testLine = `${words[n]} `;
      }
      else {
        line += `${words[n]} `;
      }
      if(n === words.length - 1) {
        lineArray.push(line);
      }
    }
    // Return the line array
    return lineArray;
  }



}
document.addEventListener("DOMContentLoaded", function() {
  new ChartClass();
});
